<script setup lang="ts">
import { VdSelect } from "@voodoo.io/voodoo-ui";
import { computed, ref, PropType } from "vue";

type Instance = {
  id: string;
  name: string;
  enabled: boolean;
};

const SELECT_VALUES = {
  ALL: { label: "Select All", value: "all" },
  NONE: { label: "Unselect All", value: "none" },
};

const emit = defineEmits<{ (e: "change", value: string[]): void }>();

const props = defineProps({
  instances: {
    type: Array as PropType<Instance[]>,
    required: true,
  },
});

const selectedInstances = ref<string[]>([]);
const toggleAll = ref(SELECT_VALUES.ALL);

const choices = computed(() => {
  const res = props.instances.map(({ id, name }, index) => ({ label: name ?? `Instance ${index + 1}`, value: id }));

  return [toggleAll.value, ...res];
});

const onSelectionChange = (selection: string[]) => {
  if (selection.includes("all")) {
    selectedInstances.value = choices.value.filter(({ value }) => value !== "all").map(({ value }) => value);
    /**
     * Delete select all option and add select none
     */
    toggleAll.value = SELECT_VALUES.NONE;
    emit("change", selectedInstances.value);
    return;
  }

  if (selection.includes("none")) {
    selectedInstances.value = [];
    toggleAll.value = SELECT_VALUES.ALL;
    emit("change", selectedInstances.value);
    return;
  }

  /**
   * If all instances have been selected by hand, toggle the "select all" option
   */
  if (selection.length === choices.value.length - 1) {
    toggleAll.value = SELECT_VALUES.NONE;
  }

  emit("change", selectedInstances.value);
};
</script>

<template>
  <VdSelect
    v-model="selectedInstances"
    :choices="choices"
    multiple
    collapse-tags
    placeholder="Filter instances"
    class="instances-filter-component"
    @update:model-value="onSelectionChange"
  />
</template>
